/**
 * <TutorLiveCenterHead> — la ligne du centre du live du masteur : qui l'on regarde, les onglets, et les
 * gestes sur son activité (molecule).
 *
 * **La ligne d'objectif, en tête** (Wajih, round 4 : « le tuteur ne doit pas se tromper sur l'objectif de
 * séance. C'est sur cette indication-là que le tuteur peut cliquer et accéder au profil de l'élève ») :
 * une ligne fine, portée par la couleur de la matière, sur tous les lives — « Léa · 4ᵉ · Maths ·
 * Préparer le contrôle sur les fractions » (`objective`) —, le rattrapage et un drapeau de présence ; son
 * toucher ouvre le profil et le brief (`onOpenProfile`). Au bout, le prochain contrôle en puce
 * (« Contrôle J-3 ») qui ouvre les contrôles au centre, et « Quiz d'abord ».
 *
 * **Puis une ligne au-dessus du contenu** (round 3 : « le contenu au centre doit avoir la place
 * principale ») : les onglets (`tabs`, une fente) et les gestes, ramenés à l'action qu'attend le statut
 * de l'activité (`primary` : Valider, Redonner, Soumettre), « Montrer à l'élève », « Présenter », et
 * « Plus ». Chaque geste garde son mot (round 4 : « les icônes doivent aussi avoir un texte ») : un
 * centre étroit prend le mot court ; plus étroit encore (`oneLine` faux, la tête posée seulement : la tête
 * flottante du live garde toujours deux lignes), les onglets passent dessous, et les gestes passent à la ligne
 * quand ils ne tiennent pas : tous se voient, aucun n'est coupé au bord (au
 * téléphone, la bascule et les gestes d'une activité ouverte).
 *
 * Pendant une présentation, la barre de dessin et « Arrêter » prennent la place des gestes. Au téléphone,
 * la bascule Dialogue · Concentré de l'élève regardé s'y pose aussi (`mode`). Les gestes sur l'activité
 * (l'action du statut, « Montrer à l'élève », « Présenter », ⋮) se tiennent : à l'étroit, ils passent à
 * la ligne ensemble, ⋮ jamais seul sur sa ligne.
 *
 * La présentation seule (`presentationOnly`, l'écran entier part chez les élèves) : la tête ne garde que
 * le titre de l'activité, la barre de dessin et « Arrêter » — la règle pure est `tutorLiveCenterHeadParts`.
 *
 * Contrôlée : tout arrive en props, chaque geste repart.
 */
import React from "react"
import { Pressable, View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type UIActivityDurationTone, type UIActivityStatus } from "../../../ports/primary/ui/types/activity"
import { type UISubject } from "../../../ports/primary/ui/types/schoolSubject"
import { type UISoundMode } from "../../../ports/primary/ui/types/studentLive"
import { type UITutorActivityAction } from "../../../ports/primary/ui/types/tutorEleves"
import { type UITutorLiveFocus, type UITutorLiveStudent } from "../../../ports/primary/ui/types/tutorLive"
import { Avatar } from "../../atoms/Avatar"
import { Badge } from "../../atoms/Badge"
import { Button } from "../../atoms/Buttons/Button"
import { ChoiceChip } from "../../atoms/ChoiceChip"
import { Row } from "../../atoms/containers/Row"
import { type IconName } from "../../atoms/icons/Icon/iconRegistry"
import { Text } from "../../atoms/typography/Text"
import { SubjectBadge } from "../../atoms/SubjectBadge"
import { ActivityStatusBadge } from "../ActivityStatusBadge"
import { TutorAnnotationToolbar, type TutorAnnotationPen, type TutorAnnotationVisual } from "../TutorAnnotationToolbar"
import { TutorQuizFirstMention } from "../TutorQuizFirstMention"
import { TutorSoundModeSwitch } from "../TutorSoundModeSwitch"
import { tutorStudentGrade } from "../TutorStudentChips"
import { ACTIVITY_ACTIONS, ACTIVITY_SHOWN_LABEL, ACTIVITY_SHOWN_SHORT } from "./tutorActivityActions"
import { type SemanticColorKey } from "../../theme/tokens/colors"
import { formatDaysUntil } from "../../../ports/primary/ui/formatters/dateFormatter"
import { subjectTint } from "../../utils/subjectTouch"
import { tutorLiveCenterHeadParts } from "./tutorLiveCenterHeadParts"

/**
 * L'activité ouverte, en tête de ses gestes : son chapitre (à défaut la matière) et son chrono (l'ancienne
 * `ActivityItemInfo`). Le ton du chrono : orange (`over`) au-delà du temps prévu, vert en deçà.
 */
export type TutorLiveActivityInfo = {
    chapter?: string
    chrono?: { label: string; tone?: UIActivityDurationTone }
}

/** Le chrono, au ton du temps prévu (comme `ActivityRow`). */
const CHRONO_VARIANT = { on_time: "success", over: "warning" } as const

/** « Présenter », et la barre de dessin pendant. */
export type TutorLivePresent = {
    on: boolean
    pending?: boolean
    onToggle: () => void
    annotation?: {
        drawing: boolean
        canUndo?: boolean
        onToggleDraw: () => void
        onUndo: () => void
        onClear: () => void
        /** L'encre et la gomme, comme au tableau blanc. Absent : le trait de toujours. */
        pen?: TutorAnnotationPen
    }
    /**
     * « Montrer à l'élève » reste à côté de la barre de dessin pendant la présentation, comme dans
     * l'ancienne fiche (`TodoModal`). Défaut `false` : la barre le remplace.
     */
    keepShow?: boolean
    /**
     * Pendant la présentation, ce que le masteur doit savoir de son public (« 2 élèves en Concentré ne
     * voient pas la présentation ») et, s'il y a lieu, le geste qui y répond (« Tous en Dialogue »). Rendu
     * par `<TutorLiveCenter>` hors de la région partagée ; il ne nomme jamais un élève (au centre seul,
     * tout l'écran part). Absent (défaut) : rien.
     */
    audienceNote?: { text: string; action?: { label: string; onPress: () => void } }
}

export type TutorLiveCenterHeadProps = {
    student?: UITutorLiveStudent
    /** L'activité ouverte au centre. Absente : l'élève seul. */
    focus?: UITutorLiveFocus | null
    /** L'action qu'attend le statut de l'activité (Valider, Redonner, Soumettre). Absente : rien. */
    primary?: { action: UITutorActivityAction; onPress: () => void; pending?: boolean }
    /**
     * « Montrer à l'élève ». Absent : pas de bouton. `pressableWhenShown` : « Montrée à l'élève »
     * reste touchable (l'écran dit alors que l'élève l'a déjà devant lui) ; défaut `false`, le bouton se grise.
     */
    show?: { onPress: () => void; pending?: boolean; pressableWhenShown?: boolean }
    /** « Présenter » (le web, pas le téléphone). Absent : pas de bouton. */
    present?: TutorLivePresent
    /** ⋮ : les autres gestes sur l'activité. Absent : pas de menu. */
    onOpenMenu?: () => void
    /** Au téléphone : la bascule de l'élève regardé. */
    mode?: { onChange: (mode: UISoundMode) => void; pending?: boolean }
    /** Le visage et le prénom ouvrent le profil de l'élève et son brief. Absent : rien ne presse. */
    onOpenProfile?: () => void
    /**
     * L'objectif de la séance (tous les lives) : ses matières, ce qu'il vise, et le prochain contrôle en puce
     * (`assessment`, ouvert quand « Contrôles » est au centre). Sans matière : « Matière à définir ». Absent :
     * la ligne ne dit que l'élève.
     */
    objective?: {
        subjects: UISubject[]
        label?: string
        assessment?: { daysUntil: number; open?: boolean; onPress: () => void }
    }
    /** « Quiz d'abord », ses options dans son menu. */
    quizFirst?: { onRemove: () => void; removing?: boolean } | null
    /** Le statut de l'activité ouverte, avant ses gestes (l'ancienne fiche l'écrivait). Absent : rien. */
    status?: UIActivityStatus
    /**
     * L'activité ouverte, en tête de ses gestes : son titre, puis son chapitre et son chrono dessous. Absente
     * (défaut) : rien.
     */
    activityInfo?: TutorLiveActivityInfo
    /** Les onglets du centre, au milieu de la ligne. */
    tabs?: React.ReactNode
    /**
     * Les vues de l'activité ouverte, sur une seconde ligne à elles, jamais fusionnée avec la première (Wajih, 05/11) :
     * la tête flottante seulement. Absentes (défaut) : une ligne.
     */
    views?: React.ReactNode
    /** Tout tient sur une ligne. Faux : les onglets passent dessous (sauf en tête flottante, toujours sur deux lignes). Défaut `true`. */
    oneLine?: boolean
    /** Un centre étroit : « Montrer à l'élève » prend son mot court. Défaut `false`. */
    compact?: boolean
    /**
     * Ni visage, ni prénom, ni classe, ni drapeau, ni ce que vise l'élève, ni son contrôle (ni le prénom que
     * lit la bascule du son) : l'onglet entier est partagé (une présentation sans recadrage, dès son départ),
     * les élèves voient la tête. Les matières restent. Défaut `false`.
     */
    hideIdentity?: boolean
    /**
     * L'écran entier part chez les élèves (le natif, un onglet non recadré) : la tête ne garde que ce qui sert
     * la présentation — le titre de l'activité, la barre de dessin, « Arrêter », les onglets —, ni la ligne
     * d'objectif, ni la bascule du son, ni le statut, ni les gestes sur l'activité, ni ⋮. Défaut `false`.
     */
    presentationOnly?: boolean
    /**
     * Pendant une présentation du tableau blanc, la bascule de son visuel et l'avis du masteur, dans la barre
     * de dessin (l'ancien `WhiteboardTab` les gardait pendant la présentation). Absent (défaut) : la barre seule.
     */
    visual?: TutorAnnotationVisual
    /**
     * Les gestes sur l'activité sont dans la barre du bas du centre (`<TutorLiveActionBar>`, 04/11), présentation
     * comprise : la tête ne garde que l'élève et les onglets sur la première ligne, toujours (l'élève cède sa place), les vues de l'activité dessous (`views`), et le
     * titre de l'activité quand l'écran entier part (`presentationOnly`). Défaut `false` : la tête d'avant (le
     * téléphone), qui porte ses gestes.
     */
    floating?: boolean
    testID?: string
}

const TEXTS = {
    recovery: "Rattrapage",
    present: "Présenter",
    stop: "Arrêter",
    more: "Plus d'actions sur l'activité",
    profile: (firstname: string, objective?: string) =>
        [firstname, objective, `ouvrir son profil et son brief`].filter(Boolean).join(", "),
    noSubject: "Matière à définir",
    assessment: (days: number) => `Contrôle ${formatDaysUntil(days, true)}`,
    menu: "Plus",
}

const FACE_SIZE = 28
/** La ligne de l'activité ouverte ne prend pas la place des onglets. */
const INFO_MAX = 240

export function TutorLiveCenterHead(props: TutorLiveCenterHeadProps) {
    const {
        student,
        focus,
        primary,
        show,
        present,
        onOpenMenu,
        mode,
        onOpenProfile,
        objective,
        quizFirst,
        status,
        activityInfo,
        tabs,
        views,
        oneLine = true,
        compact = false,
        hideIdentity = false,
        presentationOnly = false,
        visual,
        floating = false,
        testID,
    } = props
    if (student == null) return null
    const shown = focus?.shownToStudent === true
    // Pendant une présentation, la barre de dessin prend la place des gestes.
    const annotation = present?.on === true ? present.annotation : undefined
    const parts = tutorLiveCenterHeadParts({
        focus: focus != null,
        activityInfo: activityInfo != null,
        present: present != null,
        toolbar: present != null && annotation != null,
        keepShow: present?.keepShow === true,
        status: status != null,
        primary: primary != null,
        show: show != null,
        mode: mode != null,
        menu: onOpenMenu != null,
        hideIdentity,
        presentationOnly,
    })

    const lead = objective?.subjects[0]
    const strip = parts.strip ? (
        <Row gap="2" align="center" style={floating ? styles.stripShared : styles.stripRow}>
            <Pressable
                onPress={onOpenProfile}
                disabled={onOpenProfile == null}
                accessibilityRole="button"
                accessibilityLabel={
                    !parts.identity
                        ? undefined
                        : TEXTS.profile(
                              student.firstname,
                              [
                                  tutorStudentGrade(student),
                                  ...(objective?.subjects.map((subject) => subject.name) ?? []),
                                  objective?.label,
                                  student.flag?.label,
                              ]
                                  .filter(Boolean)
                                  .join(" · ")
                          )
                }
                style={({ pressed }) => [styles.strip(lead?.color, !floating), pressed && styles.pressed]}
            >
                {parts.identity && (
                    <>
                        <Avatar uri={student.avatarUrl} name={student.firstname} size={FACE_SIZE} />
                        <Text weight="bold" color="text-strong" numberOfLines={1} style={styles.name}>
                            {student.firstname}
                        </Text>
                        <Text size="text-sm" color="text-muted">
                            {tutorStudentGrade(student)}
                        </Text>
                        {student.recovery === true && (
                            <Badge text={TEXTS.recovery} variant="neutral" truncate={false} />
                        )}
                        {student.flag != null && (
                            <Badge text={student.flag.label} variant={student.flag.tone} tinted truncate={false} />
                        )}
                    </>
                )}
                {objective != null &&
                    (objective.subjects.length > 0 ? (
                        objective.subjects.map((subject) => (
                            <SubjectBadge key={String(subject.id)} subject={subject} showLabel fullLabel />
                        ))
                    ) : (
                        // La chip se cale en haut de sa rangée (`alignSelf`) : sa boîte la recentre (daily du 04/11).
                        <View style={styles.centered}>
                            <Badge text={TEXTS.noSubject} variant="warning" tinted truncate={false} />
                        </View>
                    ))}
                {objective?.label != null && parts.identity && (
                    // À l'étroit (un téléphone), l'objectif passe sur deux lignes plutôt que de se couper.
                    <Text size="text-sm" color="text-primary" numberOfLines={oneLine ? 1 : 2} style={styles.label}>
                        {objective.label}
                    </Text>
                )}
            </Pressable>
            {objective?.assessment != null && parts.identity && (
                <ChoiceChip
                    label={TEXTS.assessment(objective.assessment.daysUntil)}
                    iconName="calendar-alt"
                    action
                    selected={objective.assessment.open === true}
                    expanded={objective.assessment.open === true}
                    onPress={objective.assessment.onPress}
                />
            )}
            {quizFirst != null && focus != null && (
                <TutorQuizFirstMention onRemove={quizFirst.onRemove} removing={quizFirst.removing} />
            )}
        </Row>
    ) : null

    const showAction =
        show != null ? (
            <HeadAction
                label={shown ? ACTIVITY_SHOWN_LABEL : ACTIVITY_ACTIONS.show.label}
                shortLabel={compact ? (shown ? ACTIVITY_SHOWN_SHORT : ACTIVITY_ACTIONS.show.short) : undefined}
                iconName={ACTIVITY_ACTIONS.show.icon}
                done={shown}
                pending={show.pending === true}
                disabled={shown && show.pressableWhenShown !== true}
                onPress={show.onPress}
            />
        ) : null

    const info =
        focus != null && parts.info != null ? (
            <View style={styles.info}>
                <Text size="text-sm" weight="bold" color="text-strong" numberOfLines={1}>
                    {focus.activityTitle}
                </Text>
                {/* La présentation seule : le titre, sans le chapitre ni le chrono de l'élève. */}
                {parts.info === "full" && activityInfo != null && (
                    <Row gap="1" align="center">
                        {activityInfo.chapter != null && (
                            <Text size="text-xs" color="text-muted" numberOfLines={1} style={styles.chapter}>
                                {activityInfo.chapter}
                            </Text>
                        )}
                        {activityInfo.chrono != null && (
                            <Badge
                                text={activityInfo.chrono.label}
                                variant={
                                    activityInfo.chrono.tone != null
                                        ? CHRONO_VARIANT[activityInfo.chrono.tone]
                                        : "neutral"
                                }
                                iconName="stopwatch"
                                tinted
                                truncate={false}
                            />
                        )}
                    </Row>
                )}
            </View>
        ) : null

    // Chaque partie n'est vraie que si sa prop est donnée (`tutorLiveCenterHeadParts`). En tête flottante, les gestes
    // sont dans la barre du bas : la tête n'en porte aucun.
    const hasActions = !floating && (parts.present != null || parts.status || parts.primary || parts.show || parts.menu)
    const actions = (
        <>
            {parts.present === "toolbar" && present != null && annotation != null && (
                <TutorAnnotationToolbar
                    drawing={annotation.drawing}
                    canUndo={annotation.canUndo}
                    onToggleDraw={annotation.onToggleDraw}
                    onUndo={annotation.onUndo}
                    onClear={annotation.onClear}
                    pen={annotation.pen}
                    onStop={present.onToggle}
                    stopping={present.pending === true}
                    visual={visual}
                />
            )}
            {parts.status && status != null && <ActivityStatusBadge status={status} />}
            {parts.primary && primary != null && (
                <HeadAction
                    label={ACTIVITY_ACTIONS[primary.action].label}
                    iconName={ACTIVITY_ACTIONS[primary.action].icon}
                    solid
                    pending={primary.pending === true}
                    onPress={primary.onPress}
                />
            )}
            {parts.show && showAction}
            {parts.present === "button" && present != null && (
                <HeadAction
                    label={present.on ? TEXTS.stop : TEXTS.present}
                    iconName="chalkboard-teacher"
                    danger={present.on}
                    pending={present.pending === true}
                    onPress={present.onToggle}
                />
            )}
            {parts.menu && onOpenMenu != null && (
                <Button
                    label={TEXTS.menu}
                    iconName="ellipsis-h"
                    variant="ghost"
                    size="sm"
                    onPress={onOpenMenu}
                    accessibilityLabel={TEXTS.more}
                />
            )}
        </>
    )

    const gestures = (
        <Row gap="2" align="center" style={oneLine ? undefined : styles.wrap}>
            {(!floating || presentationOnly) && info}
            {parts.mode && mode != null && (
                <TutorSoundModeSwitch
                    mode={student.soundMode}
                    onChange={mode.onChange}
                    pending={mode.pending}
                    disabled={student.presence !== "here"}
                    studentName={hideIdentity ? undefined : student.firstname}
                />
            )}
            {/* Les gestes sur l'activité se tiennent : à l'étroit, ils passent à la ligne ensemble, et ⋮
                reste avec eux (seul sur sa ligne, il ne disait plus à quoi il servait). */}
            {hasActions && (
                <Row gap="2" align="center" style={oneLine ? undefined : styles.actions}>
                    {actions}
                </Row>
            )}
        </Row>
    )

    const hasGestures = hasActions || (!floating && info != null) || (parts.mode && mode != null)
    if (floating) {
        // Deux lignes, jamais fusionnées (Wajih, 05/11) : l'élève, ses onglets et celui de l'activité (et la présentation
        // en cours au bout), puis les vues de l'activité. À l'étroit, l'élève cède sa place avant les onglets.
        return (
            <View style={styles.lines} testID={testID}>
                <View style={styles.line}>
                    {strip}
                    <View style={styles.tabs}>{tabs}</View>
                    {hasGestures && gestures}
                </View>
                {views}
            </View>
        )
    }

    return (
        <View style={styles.lines} testID={testID}>
            {strip}
            {oneLine ? (
                <View style={styles.line}>
                    <View style={styles.tabs}>{tabs}</View>
                    {gestures}
                </View>
            ) : (
                <>
                    {/* À l'étroit (un téléphone), les gestes passent à la ligne plutôt que d'être coupés au
                        bord : une rangée qui défile ne montrait pas qu'il en restait. */}
                    {gestures}
                    {tabs}
                </>
            )}
        </View>
    )
}

/** Un geste de la tête : le secondaire du livre, avec son mot court quand le centre est étroit. */
function HeadAction(props: {
    label: string
    /** Le mot court, quand le centre est étroit ; le lecteur d'écran garde `label`. */
    shortLabel?: string
    iconName?: IconName
    /** L'action qu'attend le statut : le principal. */
    solid?: boolean
    danger?: boolean
    pending: boolean
    disabled?: boolean
    /** L'action est déjà faite : la forme « déjà fait » (`<Button done>`). */
    done?: boolean
    onPress: () => void
}) {
    const {
        label,
        shortLabel,
        iconName,
        solid = false,
        danger = false,
        pending,
        disabled = false,
        done = false,
        onPress,
    } = props
    return (
        <Button
            label={shortLabel ?? label}
            accessibilityLabel={label}
            iconName={iconName}
            variant={solid ? "solid" : "outline"}
            color={danger ? "danger" : undefined}
            size="sm"
            loading={pending}
            disabled={disabled}
            done={done}
            onPress={onPress}
        />
    )
}

const styles = StyleSheet.create((theme) => ({
    centered: { alignSelf: "center" },
    line: { flexDirection: "row", alignItems: "center", gap: theme.spacing["3"], minHeight: 36 },
    lines: { gap: theme.spacing["2"] },
    stripRow: { minWidth: 0 },
    /**
     * L'élève et les onglets sur la même ligne : l'élève cède sa place le premier (trois fois plus vite que les
     * onglets), jamais au-delà de 40 % de la ligne.
     */
    stripShared: { minWidth: 0, flexShrink: 3, maxWidth: "40%" },
    /** La ligne d'objectif : la teinte de la matière (`subjectTint`), comme une ligne d'activité ; pas de liseré. */
    strip: (subject: SemanticColorKey | undefined, grow: boolean) => ({
        // Elle se coupe à sa place : ses puces ne débordent jamais sur « Contrôle », « Quiz d'abord » ni les onglets.
        ...(grow ? { flex: 1 } : { flexShrink: 1 }),
        overflow: "hidden" as const,
        minWidth: 0,
        flexDirection: "row" as const,
        alignItems: "center" as const,
        gap: theme.spacing["2"],
        minHeight: 36,
        paddingHorizontal: theme.spacing["2"],
        borderRadius: theme.radius.md,
        backgroundColor: subject != null ? subjectTint(theme.colors[subject]) : theme.colors["surface-subtle"],
    }),
    pressed: { opacity: 0.7 },
    label: { flexShrink: 1, minWidth: 0 },
    /**
     * Les onglets partent de leur largeur (pas de zéro) : à l'étroit, ils se partagent le manque avec l'élève au lieu de
     * déborder de la carte (Wajih, 05/11 : à 1 024 px, l'onglet de l'activité passait sous la colonne de droite).
     */
    tabs: { flexGrow: 1, flexShrink: 1, flexBasis: "auto", minWidth: 0 },
    /** Le prénom ne cède jamais sa place. */
    name: { flexShrink: 0 },
    /** L'activité ouverte, en tête des gestes : elle cède sa place avant eux. */
    info: { flexShrink: 1, minWidth: 0, maxWidth: INFO_MAX },
    chapter: { flexShrink: 1 },
    /** À l'étroit : ce qui ne tient pas sur la ligne passe dessous, entier. */
    wrap: { flexWrap: "wrap" as const, rowGap: theme.spacing["2"] },
    /**
     * Les gestes sur l'activité, ensemble : ils passent à la ligne d'un bloc, et ne se replient entre eux que
     * s'ils ne tiennent pas sur une ligne entière.
     */
    actions: { flexWrap: "wrap" as const, rowGap: theme.spacing["2"], flexShrink: 1, minWidth: 0, maxWidth: "100%" },
}))
